import type { KeyboardEvent } from 'react';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';

/** Max length per product spec (first / last name). */
export const PROFILE_NAME_MAX_LENGTH = 30;

/**
 * Allowed: letters in any script, combining marks, Unicode space separators,
 * ASCII apostrophe ('), U+2019 right single quotation, hyphen-minus (-).
 * Not allowed: digits, @#$% and other punctuation, underscores, etc.
 */
const PROFILE_NAME_PATTERN = /^[\p{L}\p{M}\p{Zs}'\u2019\-]+$/u;

/** Removes any character not allowed in profile first/last name (same rules as validation). */
const DISALLOWED_NAME_CHARS = /[^\p{L}\p{M}\p{Zs}'\u2019\-]/gu;

export function sanitizeProfileNameInput(raw: string): string {
  return (raw ?? '').replace(DISALLOWED_NAME_CHARS, '').slice(0, PROFILE_NAME_MAX_LENGTH);
}

/** Blocks typing keys that would insert disallowed characters (paste still handled via onChange sanitize). */
export function profileNameInputKeyDown(e: KeyboardEvent<HTMLElement>): void {
  if (e.nativeEvent.isComposing) return;
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const k = e.key;
  if (k.length !== 1) return;
  if (!/^[\p{L}\p{M}\p{Zs}'\u2019\-]$/u.test(k)) {
    e.preventDefault();
  }
}

/**
 * Header display: full first name + first letter of last name + dot (e.g. "Asem S.").
 */
export function formatProfileNameForHeader(first?: string | null, last?: string | null): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  if (!f && !l) return '';
  const initial =
    l.length > 0 ? `${l[0].toLocaleUpperCase(undefined)}.` : '';
  if (!f) return initial;
  return initial ? `${f} ${initial}` : f;
}

export function validateProfileNameField(raw: string): true | string {
  const value = raw ?? '';
  if (!value.trim()) {
    return REQUIRED_VALIDATION_TEXT;
  }
  if (value.length > PROFILE_NAME_MAX_LENGTH) {
    return `Use at most ${PROFILE_NAME_MAX_LENGTH} characters`;
  }
  if (!PROFILE_NAME_PATTERN.test(value)) {
    return 'Only letters, spaces, hyphens, and apostrophes are allowed';
  }
  return true;
}
